/**
 * ExecutionContext — 执行状态上下文。
 *
 * 仅 PluginBlock、ForBlock header 等需要显示执行 UI 的组件消费。
 * 与 DocContext 分离，避免执行状态更新导致所有组件 re-render。
 */

import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import type {
    ContentExecutionEntry,
    ForExecutionEntry,
    ImportExecutionEntry,
    ExecutionStatus,
    UseExecutionEntry,
} from "../../../store/plugin-execution-store";

/* ── Value ── */

export interface DefinitionExecutionState {
    isPinned: boolean;
    isRunning: boolean;
    status?: ExecutionStatus;
    duration?: number;
    computedAt?: number;
    progress?: { message: string; percent?: number };
    errorMessage?: string | null;
}

export interface ExecutionContextValue {
    pinnedDefinitions?: Set<string>;
    runningDefinitions?: Set<string>;
    executionStatuses?: Map<string, "idle" | "running" | "success" | "error" | "cached">;
    executionDurations?: Map<string, number>;
    executionComputedAts?: Map<string, number>;
    executionProgress?: Map<string, { message: string; percent?: number }>;
    executionErrors?: Map<string, string>;
    forExecutions?: Map<string, ForExecutionEntry>;
    contentExecutions?: Map<string, ContentExecutionEntry>;
    importExecutions?: Map<string, ImportExecutionEntry>;
    useExecutions?: Map<string, UseExecutionEntry>;
    /** Whether the engine session is established for this tab */
    sessionConnected?: boolean;
    /**
     * Optional per-definition external subscription. Preview uses this to avoid
     * pushing whole execution maps through context on every progress/status tick.
     */
    getDefinitionExecutionState?: (definitionId: string) => DefinitionExecutionState;
    subscribeDefinitionExecution?: (
        definitionId: string,
        onStoreChange: () => void,
    ) => () => void;
    getForExecution?: (forId: string) => ForExecutionEntry | undefined;
    subscribeForExecution?: (
        forId: string,
        onStoreChange: () => void,
    ) => () => void;
    getContentExecution?: (contentId: string) => ContentExecutionEntry | undefined;
    subscribeContentExecution?: (
        contentId: string,
        onStoreChange: () => void,
    ) => () => void;
    getImportExecution?: (namespace: string) => ImportExecutionEntry | undefined;
    subscribeImportExecution?: (
        namespace: string,
        onStoreChange: () => void,
    ) => () => void;
    getUseExecution?: (useId: string) => UseExecutionEntry | undefined;
    subscribeUseExecution?: (
        useId: string,
        onStoreChange: () => void,
    ) => () => void;
}

/* ── Context ── */

const ExecutionContext = createContext<ExecutionContextValue | null>(null);
const EMPTY_DEFINITION_EXECUTION_STATE: DefinitionExecutionState = {
    isPinned: false,
    isRunning: false,
    errorMessage: null,
};

export const ExecutionContextProvider = ExecutionContext.Provider;

/* ── Hook ── */

/**
 * 获取执行状态上下文。在 DocumentRenderer（Play/VSCode）中可能为 null，
 * 因为 store-free 管线可能不提供执行状态。
 */
export function useExecutionContext(): ExecutionContextValue | null {
    return useContext(ExecutionContext);
}

function readDefinitionExecutionStateFromContext(
    ctx: ExecutionContextValue | null,
    definitionId: string,
): DefinitionExecutionState {
    if (!ctx) return EMPTY_DEFINITION_EXECUTION_STATE;
    const status = ctx.executionStatuses?.get(definitionId);
    const duration = ctx.executionDurations?.get(definitionId);
    const computedAt = ctx.executionComputedAts?.get(definitionId);
    const progress = ctx.executionProgress?.get(definitionId);
    return {
        isPinned: ctx.pinnedDefinitions?.has(definitionId) ?? false,
        isRunning: ctx.runningDefinitions?.has(definitionId) ?? status === "running",
        ...(status ? { status } : {}),
        ...(duration != null ? { duration } : {}),
        ...(computedAt != null ? { computedAt } : {}),
        ...(progress ? { progress } : {}),
        errorMessage: ctx.executionErrors?.get(definitionId) ?? null,
    };
}

export function useDefinitionExecutionState(
    definitionId: string,
): DefinitionExecutionState {
    const ctx = useContext(ExecutionContext);
    const subscribeDefinitionExecution = ctx?.subscribeDefinitionExecution;
    const getDefinitionExecutionState = ctx?.getDefinitionExecutionState;

    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            subscribeDefinitionExecution
                ? subscribeDefinitionExecution(definitionId, onStoreChange)
                : () => { },
        [definitionId, subscribeDefinitionExecution],
    );
    const getSnapshot = useCallback(
        () =>
            getDefinitionExecutionState
                ? getDefinitionExecutionState(definitionId)
                : EMPTY_DEFINITION_EXECUTION_STATE,
        [definitionId, getDefinitionExecutionState],
    );

    const externalState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    if (getDefinitionExecutionState) return externalState;
    return readDefinitionExecutionStateFromContext(ctx, definitionId);
}

function useExecutionEntry<T>(
    id: string | null | undefined,
    getExternal: ((id: string) => T | undefined) | undefined,
    subscribeExternal:
        | ((id: string, onStoreChange: () => void) => () => void)
        | undefined,
    fallbackEntry: T | undefined,
): T | undefined {
    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            id && subscribeExternal
                ? subscribeExternal(id, onStoreChange)
                : () => { },
        [id, subscribeExternal],
    );
    const getSnapshot = useCallback(
        () => id && getExternal ? getExternal(id) : undefined,
        [id, getExternal],
    );

    const externalEntry = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    return getExternal ? externalEntry : fallbackEntry;
}

export function useForExecution(
    forId: string | null | undefined,
): ForExecutionEntry | undefined {
    const ctx = useContext(ExecutionContext);
    return useExecutionEntry(
        forId,
        ctx?.getForExecution,
        ctx?.subscribeForExecution,
        forId ? ctx?.forExecutions?.get(forId) : undefined,
    );
}

export function useContentExecution(
    contentId: string | null | undefined,
): ContentExecutionEntry | undefined {
    const ctx = useContext(ExecutionContext);
    return useExecutionEntry(
        contentId,
        ctx?.getContentExecution,
        ctx?.subscribeContentExecution,
        contentId ? ctx?.contentExecutions?.get(contentId) : undefined,
    );
}

export function useImportExecution(
    namespace: string | null | undefined,
): ImportExecutionEntry | undefined {
    const ctx = useContext(ExecutionContext);
    return useExecutionEntry(
        namespace,
        ctx?.getImportExecution,
        ctx?.subscribeImportExecution,
        namespace ? ctx?.importExecutions?.get(namespace) : undefined,
    );
}

export function useUseExecution(
    useId: string | null | undefined,
): UseExecutionEntry | undefined {
    const ctx = useContext(ExecutionContext);
    return useExecutionEntry(
        useId,
        ctx?.getUseExecution,
        ctx?.subscribeUseExecution,
        useId ? ctx?.useExecutions?.get(useId) : undefined,
    );
}

export function useExecutionSessionConnected(): boolean {
    return useContext(ExecutionContext)?.sessionConnected ?? false;
}
